import React, { useState, useRef } from 'react';
import Barcode from 'react-barcode';
import QRCode from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

const PrintBarcodePage: React.FC = () => {
  const [barcodeValue, setBarcodeValue] = useState<string>('1234567890123');
  const [title, setTitle] = useState<string>('Ürün Barkodu');
  const [description, setDescription] = useState<string>('Ürün açıklaması');
  const componentRef = useRef<HTMLDivElement>(null);

  // PNG olarak indir - html2canvas ile
  const handleDownloadPNG = async () => {
    if (componentRef.current) {
      try {
        // html2canvas kullanarak daha iyi kalitede bir görüntü oluştur
        const canvas = await html2canvas(componentRef.current, {
          scale: 3, // Daha yüksek kalite için ölçeklendirme
          useCORS: true, // CORS sorunlarını önlemek için
          allowTaint: true, // Harici içeriğe izin ver
          backgroundColor: '#ffffff', // Beyaz arka plan
          logging: false // Konsol mesajlarını devre dışı bırak
        });
        
        // Canvas'ı PNG olarak kaydet
        const dataUrl = canvas.toDataURL('image/png');
        saveAs(dataUrl, `barkod-${barcodeValue}.png`);
      } catch (error) {
        console.error('PNG indirme hatası:', error);
        alert('PNG oluşturulurken bir hata oluştu.');
      }
    }
  };

  // SVG olarak indir
  const handleDownloadSVG = () => {
    if (componentRef.current) {
      // QR kod ve barkod içeriğini SVG'ye dönüştür
      const svgElements = componentRef.current.querySelectorAll('svg');
      if (svgElements.length > 0) {
        // İlk SVG elementini al (QR kod)
        const qrCodeSVG = svgElements[0].outerHTML;
        
        // SVG dosyasını oluştur
        const svgBlob = new Blob([qrCodeSVG], { type: 'image/svg+xml;charset=utf-8' });
        saveAs(svgBlob, `qrcode-${barcodeValue}.svg`);
        
        // Barkod SVG'si varsa (ikinci SVG elementi)
        if (svgElements.length > 1) {
          const barcodeSVG = svgElements[1].outerHTML;
          const barcodeSvgBlob = new Blob([barcodeSVG], { type: 'image/svg+xml;charset=utf-8' });
          saveAs(barcodeSvgBlob, `barkod-${barcodeValue}.svg`);
        }
      }
    }
  };

  // PDF olarak indir - html2canvas ile
  const handleDownloadPDF = async () => {
    if (componentRef.current) {
      try {
        // html2canvas kullanarak daha iyi kalitede bir görüntü oluştur
        const canvas = await html2canvas(componentRef.current, {
          scale: 3, // Daha yüksek kalite için ölçeklendirme
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Fiş formatı için dikey PDF oluştur
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [58, 120] // 58mm genişlik (standart fiş genişliği)
        });
        
        const imgWidth = 50; // Kenar boşlukları için biraz daralttık
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 4, 4, imgWidth, imgHeight);
        pdf.save(`barkod-${barcodeValue}.pdf`);
      } catch (error) {
        console.error('PDF indirme hatası:', error);
        alert('PDF oluşturulurken bir hata oluştu.');
      }
    }
  };

  // Yazdır - html2canvas ile - etiket sayfanın tamamını kaplasın
  const handlePrint = async () => {
    if (componentRef.current) {
      try {
        // html2canvas kullanarak görüntü oluştur
        const canvas = await html2canvas(componentRef.current, {
          scale: 5, // Daha yüksek kalite için ölçeklendirme arttırıldı
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Yeni pencere aç
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Lütfen popup pencerelerine izin verin!');
          return;
        }
        
        // Yazdırma sayfasını oluştur - etiketin sayfayı kaplaması için stilller güncellendi
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Barkod Yazdır - ${barcodeValue}</title>
              <style>
                @page {
                  size: 80mm 120mm;
                  margin: 0;
                }
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  height: 100%;
                }
                body {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                }
                .print-container {
                  width: 100%;
                  height: 100%;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                }
                img {
                  width: 100%;
                  max-height: 100vh;
                  object-fit: contain;
                }
                .print-button {
                  position: fixed;
                  bottom: 20px;
                  left: 50%;
                  transform: translateX(-50%);
                  background-color: #4a86e8;
                  color: white;
                  border: none;
                  padding: 10px 20px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 16px;
                  z-index: 1000;
                }
                @media print {
                  html, body {
                    width: 100%;
                    height: 100%;
                  }
                  .print-container {
                    width: 100%;
                    height: 100%;
                  }
                  img {
                    width: 100% !important;
                    height: auto !important;
                    max-height: none;
                    object-fit: contain;
                    page-break-inside: avoid;
                  }
                  .print-button {
                    display: none;
                  }
                }
              </style>
            </head>
            <body>
              <div class="print-container">
                <img src="${imgData}" alt="Barkod: ${barcodeValue}" />
                <button class="print-button" onclick="window.print();">Yazdır</button>
              </div>
            </body>
          </html>
        `);
        
        printWindow.document.close();
      } catch (error) {
        console.error('Yazdırma hatası:', error);
        alert('Yazdırma hazırlanırken bir hata oluştu.');
      }
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Barkod Oluşturma Sistemi</h1>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Barkod Değeri:</label>
            <input
              type="text"
              value={barcodeValue}
              onChange={(e) => setBarcodeValue(e.target.value)}
              className="border rounded p-2 w-full"
              placeholder="Barkod değerini girin"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Başlık:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border rounded p-2 w-full"
              placeholder="Başlık girin"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Açıklama:</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border rounded p-2 w-full"
              placeholder="Açıklama girin"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={handlePrint}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Yazdır
        </button>
        <button
          onClick={handleDownloadPNG}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
        >
          PNG İndir
        </button>
        <button
          onClick={handleDownloadSVG}
          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded"
        >
          SVG İndir
        </button>
        <button
          onClick={handleDownloadPDF}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
        >
          PDF İndir
        </button>
      </div>

      <div className="border p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Önizleme:</h2>
        <div
          ref={componentRef}
          className="receipt-container p-3 bg-white mx-auto"
          style={{
            width: '58mm', // Standart fiş genişliği
            maxWidth: '100%',
            border: '1px dashed #ccc',
          }}
        >
          <div className="text-center space-y-2">
            <div className="font-bold text-lg">{title}</div>
            <div className="text-sm">{description}</div>
            
            {/* QR Kod üstte */}
            <div className="flex justify-center my-3">
              <QRCode value={barcodeValue} size={100} />
            </div>
            
            {/* Barkod altta */}
            <div className="flex justify-center mt-3">
              <Barcode 
                value={barcodeValue} 
                width={1.5}
                height={40}
                fontSize={12}
                marginTop={10}
                displayValue={true}
              />
            </div>
            
            <div className="text-xs mt-4">
              {new Date().toLocaleDateString('tr-TR')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintBarcodePage;